import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import TagManager from "react-gtm-module";
import { ThemeProvider } from "styled-components";

import Loading from "./pages/Loading";
import AuthProviders from "./providers/AuthProviders";
import {
  getDataFromLocalStorage,
  saveDataToLocalStorage,
} from "./api/localStorage";
import { sendMessageToExtension } from "./utils/postToExtension";
import { isStandalone } from "./utils/hostMode";
import { clearCache } from "./utils/clearCache";
import packageJSON from "../package.json";
import { gtmId, isNeedToClearCache } from "./utils/config";
import {
  isAdminSite,
  isRevIntelSite,
  applyAppBranding,
  getCurrentEnv,
} from "./utils/appConfig";
import { getTheme } from "./themes";

const MainApp = lazy(() => import("./MainApp"));
const AdminApp = lazy(() => import("./AdminApp"));
const RevIntelApp = lazy(() => import("./RevIntelApp"));

!!gtmId && TagManager.initialize({ gtmId });

// TEMP-BOT-TOGGLE: Alt+Shift+B (Option+Shift+B on a Mac) flips the RTP AI character between
// the new v5 art and the old art (remembered across reloads), for side-by-side testing. Never
// in prod. Delete every TEMP-BOT-TOGGLE line once the new art is signed off.
const LEGACY_AI_CHARACTER_KEY = "legacyAiCharacter";
const canToggleAiCharacter = getCurrentEnv() !== "prod";

const App = () => {
  const isAdminEnvironment = isAdminSite();
  const isRevIntelEnvironment = isRevIntelSite();
  // TEMP-BOT-TOGGLE
  const [isLegacyAiCharacter, setIsLegacyAiCharacter] = useState(
    () =>
      canToggleAiCharacter &&
      localStorage.getItem(LEGACY_AI_CHARACTER_KEY) === "true"
  );
  const theme = { ...getTheme(), isLegacyAiCharacter };
  const [isStorageSyncing, setIsStorageSyncing] = useState(
    !isAdminEnvironment && !isRevIntelEnvironment
  );

  useEffect(() => {
    applyAppBranding();
  }, []);

  // TEMP-BOT-TOGGLE
  useEffect(() => {
    if (!canToggleAiCharacter) {
      return;
    }
    const handleKeyDown = (event) => {
      // No Control/Command: Chrome claims Cmd+Shift+B and Cmd+Option+B for bookmarks, and
      // PC-style key remappers turn Control+B into Command+B before Chrome sees it
      if (
        event.altKey &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        event.code === "KeyB"
      ) {
        event.preventDefault();
        setIsLegacyAiCharacter((isLegacy) => !isLegacy);
      }
    };
    // Capture phase, so an input that stops propagation can't swallow the shortcut
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  // TEMP-BOT-TOGGLE
  useEffect(() => {
    if (canToggleAiCharacter) {
      localStorage.setItem(
        LEGACY_AI_CHARACTER_KEY,
        String(isLegacyAiCharacter)
      );
    }
  }, [isLegacyAiCharacter]);

  useEffect(() => {
    const checkVersion = () => {
      if (!isNeedToClearCache) {
        return;
      }
      const savedVersion = getDataFromLocalStorage("version");
      const currentVersion = packageJSON.version;

      if (savedVersion !== currentVersion) {
        clearCache();
        saveDataToLocalStorage("version", currentVersion);
        return;
      }
    };

    checkVersion();

    if (isAdminEnvironment || isRevIntelEnvironment) {
      return;
    }

    // Bounded wait: the undelivered fallback below covers "no extension", but not
    // delivered-and-no-reply (the extension answers only its main iframe or the
    // top-level page itself — never a secondary iframe — and any extension-side
    // failure mid-sync). Without a cap those cases spin on the loading screen forever.
    const STORAGE_SYNC_TIMEOUT_MS = 8000;
    let syncTimeoutId = null;

    const stopWaiting = () => {
      clearTimeout(syncTimeoutId);
      window.removeEventListener("message", handler);
      setIsStorageSyncing(false);
    };

    const handler = (event) => {
      if (!event?.data?.iframeStorageData) {
        if (window.location.pathname === "/install-hubspot-app") {
          stopWaiting();
        }
        return;
      }

      // The storage handshake writes session data (tokens, user) into localStorage,
      // so only accept it where it can legitimately come from. In an injected iframe
      // the extension's content script posts from the host page — our parent window.
      // In a TOP-LEVEL tab the content script has no iframe to target and posts into
      // the page's own window, so the legitimate message arrives with source ===
      // window (only script already running in this page can produce that — no other
      // window or frame can forge it, and same-page script already has localStorage
      // anyway). Any other source (popup, nested frame, unrelated window) is not the
      // extension. A hostile HOST page can still forge the iframe case — closing
      // that needs a nonce handshake with the extension (tracked separately).
      const expectedSource =
        window.self === window.top ? window : window.parent;
      if (event.source !== expectedSource) {
        return;
      }

      stopWaiting();
      for (const key in event.data.iframeStorageData) {
        saveDataToLocalStorage(key, event.data.iframeStorageData[key], true);
      }
    };

    // A standalone tab that already has a session keeps it: the extension's copy may
    // be stale or another account's. Without one, adopting the extension's session
    // saves signing in twice.
    if (isStandalone() && getDataFromLocalStorage("accessToken")) {
      setIsStorageSyncing(false);
      return;
    }

    window.addEventListener("message", handler);
    syncTimeoutId = setTimeout(stopWaiting, STORAGE_SYNC_TIMEOUT_MS);
    // No extension (plain browser tab, e.g. the campaign-ready email link): nothing
    // will ever answer get-storage-data, so stop waiting or the app hangs on the
    // loading screen forever.
    sendMessageToExtension({ message: "get-storage-data" }, stopWaiting);

    return () => {
      clearTimeout(syncTimeoutId);
      window.removeEventListener("message", handler);
    };
  }, [isAdminEnvironment, isRevIntelEnvironment]);

  return (
    <ThemeProvider theme={theme}>
      <AuthProviders>
        <Router>
          <Suspense fallback={<Loading />}>
            {isRevIntelEnvironment ? (
              <RevIntelApp />
            ) : isAdminEnvironment ? (
              <AdminApp />
            ) : isStorageSyncing ? (
              <Loading />
            ) : (
              <MainApp />
            )}
          </Suspense>
        </Router>
      </AuthProviders>
    </ThemeProvider>
  );
};

export default App;
